import type { FirebaseApp } from 'firebase/app';
import { initializeApp } from 'firebase/app';
import type { Messaging } from 'firebase/messaging';
import { getMessaging, onMessage, getToken } from 'firebase/messaging';
import FirebaseService from '../runtime/services/FirebaseService';

interface Firebase
{
    isActive: boolean;
    app: FirebaseApp;
    messaging: Messaging;
    init(): Promise<string>;
}

class FirebaseHelper implements Firebase
{
    public isActive: boolean;
    public app: FirebaseApp;
    public alert: Alerts;
    public log: Logger;
    public messaging: Messaging;
    private keyPair: string;
    private config: any;

    public constructor(alert: Alerts, log: Logger)
    {
        this.alert = alert;
        this.log = log;
    }

    public async init()
    {
        const { isActive, firebaseConfig, keyPair } = await this.getConfig();

        this.isActive = isActive;

        if (isActive)
        {
            const lines = firebaseConfig.split('\n').map((line: string) =>
            {
                const index = line.indexOf(':');

                if (index > -1)
                {
                    const key = line.slice(0, index);
                    const value = line.slice(index + 1);

                    return `"${key.trim()}":${value}`;
                }

                return line;
            });

            this.config = JSON.parse(lines.join(''));
            this.keyPair = keyPair;

            this.app = initializeApp(this.config);
            this.messaging = getMessaging(this.app);

            onMessage(this.messaging, (payload: any) =>
            {
                this.alert.info(payload.notification.body);
            });

            return await this.setToken();
        }
    }

    private async getConfig()
    {
        try
        {
            return await FirebaseService.getFirebaseConfig();
        }
        catch (ex)
        {
            this.log.error(ex);
        }
    }

    private async setToken()
    {
        try
        {
            const permission = await Notification.requestPermission();

            if (permission === 'granted')
            {
                const params = new URLSearchParams(this.config);

                const reg = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`);

                const token = await getToken(this.messaging, { vapidKey: this.keyPair, serviceWorkerRegistration: reg });

                return token;
            }
            else
            {
                this.log.warn('You have disabled notifications in your browser');
            }
        }
        catch (ex)
        {
            this.log.error(ex);
        }
    }
}

export default defineNuxtPlugin({
    name: 'firebase',
    dependsOn: ['alerts', 'logging'],
    async setup(nuxtApp)
    {
        const { $alert, $log } = useNuxtApp();
        const helper = new FirebaseHelper($alert, $log) as Firebase;

        return {
            provide: {
                firebase: helper
            }
        };
    }
});

declare module '@vue/runtime-core'
{
    interface ComponentCustomProperties
    {
        $firebase: Firebase;
    }
}
